import { useEffect, useState } from 'react';
import { useDisplayChannel } from './hooks/useDisplayChannel';
import { renderDisplay, type DisplayFrame } from './renderers';
import { TranscriptPane } from './components/TranscriptPane';

function sessionIdFromUrl(): string {
  return new URLSearchParams(window.location.search).get('sessionId') ?? '';
}

export function App() {
  const { status, current, transcript, title, connect } = useDisplayChannel();
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const id = sessionIdFromUrl();
    if (id) void connect(id);
  }, [connect]);

  const headerTitle = title ?? 'Unified AI — Screenshare';

  // Reflect the configurable title in the browser tab when it changes.
  useEffect(() => {
    if (title) document.title = title;
  }, [title]);

  const disconnected = status === 'ended' || status === 'error';

  return (
    <div className="h-screen bg-bg text-content flex flex-col">
      <header className="px-6 py-3 border-b border-border flex items-center justify-between">
        <h1 className="text-lg font-semibold">{headerTitle}</h1>
        <span
          className={
            disconnected ? 'text-xs text-warning' : 'text-xs text-content-muted'
          }
        >
          {status}
        </span>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Display area (LEFT): reflows to fill freed space when the pane
            collapses. Content is TOP-anchored and scrolls internally
            (`overflow-auto`) rather than vertically centered, so each
            replace-mode frame lands in the same spot and a taller frame simply
            scrolls instead of pushing/jumping the content. */}
        <main className="flex-1 min-w-0 p-6 overflow-auto">
          {current ? (
            // Replace-mode: only the latest frame is shown, rendered via the
            // generic dispatcher (Req 3.1). The hook stores the whole frame
            // ({ kind, title, theme, payload }) in `current.payload`. The
            // wrapper keeps the frame horizontally centered (`mx-auto`) while
            // anchored to the top of the scroll area.
            <div className="mx-auto w-full max-w-4xl">
              {renderDisplay(current.payload as DisplayFrame)}
            </div>
          ) : (
            // Non-frame states stay vertically centered for a tidy placeholder.
            <div className="flex h-full items-center justify-center">
              {disconnected ? (
                // Ended/disconnected with nothing shown yet (Req 3.3).
                <p className="text-warning">
                  Display {status === 'error' ? 'connection error' : 'ended'}.
                </p>
              ) : (
                // Neutral waiting state before the first frame (Req 3.2).
                <p className="text-content-muted">Waiting for display content…</p>
              )}
            </div>
          )}
        </main>

        {/* Transcript pane (RIGHT): collapsible; shrinks to a thin handle when collapsed. */}
        <TranscriptPane
          lines={transcript}
          collapsed={collapsed}
          onToggle={() => setCollapsed((c) => !c)}
        />
      </div>
    </div>
  );
}
